import React, { useEffect, useRef, useState } from "react";
import { MerchantLayout } from "@/pages/merchant/MerchantLayout";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, AlertTriangle, Camera, ImagePlus, Loader2, ScanLine } from "lucide-react";

/**
 * /merchant/coupons/scan — camera + file upload QR decoder.
 * On decode → POST /api/merchant/coupons/redeem → full-screen result.
 */
export default function MerchantScan() {
    const [mode, setMode] = useState("camera");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [manual, setManual] = useState("");
    const camDivRef = useRef(null);
    const scannerRef = useRef(null);

    const redeem = async (code) => {
        if (!code) return;
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.post("/merchant/coupons/redeem", { code: code.trim().toUpperCase() });
            const status = r.data?.status;
            if (status === "REDEEMED") {
                setResult({ kind: "ok", code, redeemed_at: r.data.redeemed_at, post_id: r.data.post_id });
            } else if (status === "ALREADY_REDEEMED") {
                setResult({ kind: "already", code, redeemed_at: r.data.redeemed_at, post_id: r.data.post_id });
            } else {
                setResult({ kind: "unknown", code, message: JSON.stringify(r.data) });
            }
        } catch (e) {
            const code2 = e?.response?.status;
            const msg = errorMessage(e);
            if (code2 === 404) setResult({ kind: "not_found", code, message: msg });
            else if (code2 === 410) setResult({ kind: "expired", code, message: msg });
            else setResult({ kind: "error", code, message: msg });
        } finally { setBusy(false); }
    };

    // Camera lifecycle
    useEffect(() => {
        let cancelled = false;
        if (mode !== "camera" || result) {
            if (scannerRef.current) {
                try { scannerRef.current.stop().catch(() => {}); scannerRef.current.clear(); } catch { /* noop */ }
                scannerRef.current = null;
            }
            return;
        }
        (async () => {
            try {
                const { Html5Qrcode } = await import("html5-qrcode");
                if (cancelled) return;
                if (!camDivRef.current) return;
                const scanner = new Html5Qrcode(camDivRef.current.id);
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 240, height: 240 } },
                    (text) => { scanner.pause(true); redeem(text); },
                    () => { /* per-frame errors are noisy — ignore */ },
                );
            } catch (e) {
                toast.error("Camera unavailable — try Upload mode or enable camera permission.");
            }
        })();
        return () => {
            cancelled = true;
            if (scannerRef.current) {
                try { scannerRef.current.stop().catch(() => {}); scannerRef.current.clear(); } catch { /* noop */ }
                scannerRef.current = null;
            }
        };
    }, [mode, result]); // eslint-disable-line

    const onFileChosen = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const { Html5Qrcode } = await import("html5-qrcode");
            const tmp = new Html5Qrcode("mscan-file-tmp", /* verbose */ false);
            const text = await tmp.scanFile(f, /* showImage */ false);
            redeem(text);
        } catch (err) {
            toast.error("Could not read QR from image. Try a clearer photo.");
        }
    };

    const scanNext = () => { setResult(null); setManual(""); };

    return (
        <MerchantLayout>
            <div className="p-4" data-testid="merchant-scan">
                <div className="mb-3">
                    <div className="text-xs font-medium text-orange-600">Merchant tools</div>
                    <h1 className="text-xl font-extrabold text-neutral-900 flex items-center gap-2"><ScanLine size={20} /> Scan coupon</h1>
                    <p className="mt-1 text-xs text-neutral-500">Point at the customer's QR — or upload a photo of it. Manual entry is available as a fallback.</p>
                </div>

                {result ? (
                    <ResultOverlay result={result} onNext={scanNext} />
                ) : (
                    <>
                        <Tabs value={mode} onValueChange={setMode}>
                            <TabsList data-testid="scan-mode-tabs">
                                <TabsTrigger value="camera" data-testid="scan-mode-camera"><Camera size={14} className="mr-2" /> Camera</TabsTrigger>
                                <TabsTrigger value="upload" data-testid="scan-mode-upload"><ImagePlus size={14} className="mr-2" /> Upload</TabsTrigger>
                            </TabsList>
                            <TabsContent value="camera" className="mt-4">
                                <Card className="overflow-hidden">
                                    <div id="mscan-cam" ref={camDivRef} className="mx-auto aspect-square w-full max-w-sm bg-black" data-testid="scan-camera-view" />
                                </Card>
                                <p className="mt-2 text-center text-[11px] text-neutral-500">Position the QR inside the guide. It will scan automatically.</p>
                            </TabsContent>
                            <TabsContent value="upload" className="mt-4">
                                <Card className="p-6">
                                    <div id="mscan-file-tmp" className="hidden" />
                                    <label className="flex cursor-pointer flex-col items-center gap-2 text-neutral-600 hover:text-orange-600" data-testid="scan-upload-label">
                                        <ImagePlus size={28} />
                                        <span className="text-sm font-semibold">Choose an image with a QR code</span>
                                        <span className="text-[11px] text-neutral-500">Supports JPEG, PNG, WEBP up to ~5MB</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={onFileChosen} data-testid="scan-upload-input" />
                                    </label>
                                </Card>
                            </TabsContent>
                        </Tabs>

                        <Card className="mt-4 p-4">
                            <Label className="text-xs">Manual entry (fallback)</Label>
                            <div className="mt-1 flex gap-2">
                                <Input value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX" data-testid="scan-manual-input" className="font-mono tracking-widest" />
                                <Button onClick={() => redeem(manual)} disabled={!manual || busy} className="nest-gradient text-white" data-testid="scan-manual-submit">
                                    {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Redeem
                                </Button>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </MerchantLayout>
    );
}

function ResultOverlay({ result, onNext }) {
    const cfg = {
        ok: { icon: <CheckCircle2 size={64} className="text-emerald-500" />, bg: "bg-emerald-50", title: "Coupon Redeemed", tone: "text-emerald-800" },
        already: { icon: <AlertTriangle size={64} className="text-amber-500" />, bg: "bg-amber-50", title: "Already Redeemed", tone: "text-amber-800" },
        not_found: { icon: <XCircle size={64} className="text-rose-500" />, bg: "bg-rose-50", title: "Invalid or foreign coupon", tone: "text-rose-800" },
        expired: { icon: <XCircle size={64} className="text-rose-500" />, bg: "bg-rose-50", title: "Coupon Expired", tone: "text-rose-800" },
        error: { icon: <XCircle size={64} className="text-rose-500" />, bg: "bg-rose-50", title: "Something went wrong", tone: "text-rose-800" },
        unknown: { icon: <AlertTriangle size={64} className="text-neutral-500" />, bg: "bg-neutral-100", title: "Unknown response", tone: "text-neutral-800" },
    }[result.kind] || {};
    return (
        <Card className={`mt-2 flex flex-col items-center gap-3 p-8 text-center ${cfg.bg}`} data-testid={`scan-result-${result.kind}`}>
            {cfg.icon}
            <div className={`text-xl font-extrabold ${cfg.tone}`}>{cfg.title}</div>
            <div className="font-mono text-sm tracking-widest text-neutral-800" data-testid="scan-result-code">{result.code}</div>
            {result.message && <div className="text-[11px] text-neutral-500">{result.message}</div>}
            {result.redeemed_at && <div className="text-[11px] text-neutral-500">at {new Date(result.redeemed_at).toLocaleString("en-IN")}</div>}
            <Button className="mt-2 nest-gradient text-white" onClick={onNext} data-testid="scan-result-next">Scan next</Button>
        </Card>
    );
}
